import { useState, useEffect, useRef } from 'react';
import { ChevronLeft, ChevronRight, Star } from 'lucide-react';

const TestimonialSlider = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [activeIndex, setActiveIndex] = useState(0);
  const scrollContainerRef = useRef(null);

  const testimonials = [
    {
      name: 'Rachel Johnson',
      role: 'Founder, Bloom Beauty Co.',
      content: 'Absolutely phenomenal service from start to finish! The team at Trademark Protocols navigated me through the entire trademark process with such ease and professionalism. Highly recommend!',
      rating: 5
    },
    {
      name: 'Marcus Kim',
      role: 'CEO, NexTech Solutions',
      content: 'Best decision I made for protecting my brand! The experts here are incredibly knowledgeable and made what seemed complex feel simple. Worth every penny and more!',
      rating: 5
    },
    {
      name: 'Amanda Peters',
      role: 'Owner, Luxe Lifestyle Brand',
      content: 'Their attention to detail is unmatched. They caught potential issues I never would have noticed and saved my brand from future complications. True professionals!',
      rating: 5
    },
    {
      name: 'Thomas Brooks',
      role: 'Director, Peak Performance Fitness',
      content: 'Lightning-fast service with impeccable results. My trademark was registered smoothly, and the communication throughout the process was outstanding. 10/10 would recommend!',
      rating: 5
    },
    {
      name: 'Nina Kumar',
      role: 'Founder, Eco Green Products',
      content: 'Having worked with other trademark services before, I can confidently say Trademark Protocols stands head and shoulders above the rest. Exceptional value and service!',
      rating: 5
    },
    {
      name: 'Jordan Lee',
      role: 'Managing Partner, Urban Eats',
      content: 'From initial consultation to final registration, every interaction was seamless. They truly care about protecting your brand and it shows in their work. Could not be happier!',
      rating: 5
    },
    {
      name: 'Christina Brown',
      role: 'Owner, Artisan Coffee House',
      content: 'Their expertise in trademark law is evident in every step. They explained everything clearly and ensured I understood the process. Professional, efficient, and affordable!',
      rating: 5
    },
    {
      name: 'Daniel Martinez',
      role: 'VP Marketing, Swift Logistics',
      content: 'What impressed me most was their proactive approach. They anticipated potential issues and addressed them before they became problems. This is how trademark services should be done!',
      rating: 5
    }
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % testimonials.length);
    }, 3000); // Faster scroll: 3 seconds instead of 5
    return () => clearInterval(timer);
  }, [testimonials.length]);

  // Handle scroll to update active dot
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const scrollLeft = container.scrollLeft;
      const cardWidth = container.scrollWidth / (testimonials.length * 3); // 3 sets of testimonials
      const index = Math.round(scrollLeft / cardWidth) % testimonials.length;
      setActiveIndex(index);
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [testimonials.length]);

  // Function to scroll to specific testimonial
  const scrollToTestimonial = (index) => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const cardWidth = container.scrollWidth / (testimonials.length * 3);
    const scrollPosition = cardWidth * index;
    
    container.scrollTo({
      left: scrollPosition,
      behavior: 'smooth'
    });
    setActiveIndex(index);
  };

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % testimonials.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  return (
    <section className="py-20 bg-white" data-testid="testimonial-section">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold mb-4">What Our Clients Say</h2>
          <p className="text-xl">Trusted by businesses across the nation</p>
        </div>

        {/* Desktop View - Single Card Slider */}
        <div className="hidden md:block max-w-4xl mx-auto relative">
          <div className="bg-primary-100 rounded-2xl p-12 shadow-xl">
            <div className="flex justify-center mb-4">
              {[...Array(testimonials[currentSlide].rating)].map((_, idx) => (
                <Star key={idx} className="w-6 h-6 fill-yellow-400 text-yellow-400" />
              ))}
            </div>
            
            <p className="text-xl text-gray-700 mb-8 italic text-center leading-relaxed">
              &ldquo;{testimonials[currentSlide].content}&rdquo;
            </p>
            
            <div className="text-center">
              <p className="font-bold text-lg text-gray-900">{testimonials[currentSlide].name}</p>
              <p className="text-secondary-600">{testimonials[currentSlide].role}</p>
            </div>
          </div>

          <button
            onClick={prevSlide}
            className="absolute left-0 top-1/2 transform -translate-y-1/2 -translate-x-4 bg-white rounded-full p-3 shadow-lg hover:bg-primary-50"
            data-testid="testimonial-prev-btn"
          >
            <ChevronLeft className="w-6 h-6 text-gray-700" />
          </button>
          
          <button
            onClick={nextSlide}
            className="absolute right-0 top-1/2 transform -translate-y-1/2 translate-x-4 bg-white rounded-full p-3 shadow-lg hover:bg-primary-50"
            data-testid="testimonial-next-btn"
          >
            <ChevronRight className="w-6 h-6 text-gray-700" />
          </button>

          <div className="flex justify-center mt-8 space-x-2">
            {testimonials.map((_, idx) => (
              <button
                key={idx}
                onClick={() => setCurrentSlide(idx)}
                className={`w-3 h-3 rounded-full transition-all ${
                  currentSlide === idx ? 'bg-primary-600 w-8' : 'bg-gray-300'
                }`}
                data-testid={`testimonial-dot-${idx}`}
              />
            ))}
          </div>
        </div>

        {/* Mobile View - Horizontal Scroll Slider */}
        <div className="md:hidden relative">
          <div 
            ref={scrollContainerRef}
            className="overflow-x-scroll snap-x snap-mandatory scrollbar-hide -mx-4 px-4"
            style={{ WebkitOverflowScrolling: 'touch' }}
          >
            <div className="flex gap-6 pb-4 min-w-max">
              {/* Show testimonials multiple times for smooth scroll experience */}
              {[...Array(3)].map((_, setIndex) => (
                testimonials.map((testimonial, idx) => (
                  <div
                    key={`${setIndex}-${idx}`}
                    className="flex-shrink-0 w-[85vw] snap-center"
                  >
                    <div className="bg-primary-100 rounded-2xl p-6 shadow-lg h-full min-h-[280px]">
                      <div className="flex justify-center mb-3">
                        {[...Array(testimonial.rating)].map((_, starIdx) => (
                          <Star key={starIdx} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                        ))}
                      </div>
                      
                      <p className="text-base text-gray-700 mb-6 italic text-center leading-relaxed">
                        &ldquo;{testimonial.content}&rdquo;
                      </p>
                      
                      <div className="text-center">
                        <p className="font-bold text-base text-gray-900">{testimonial.name}</p>
                        <p className="text-sm text-secondary-600">{testimonial.role}</p>
                      </div>
                    </div>
                  </div>
                ))
              ))}
            </div>
          </div>
          
          {/* Interactive navigation dots */}
          <div className="flex justify-center mt-6 space-x-2">
            {testimonials.map((_, idx) => (
              <button
                key={idx}
                onClick={() => scrollToTestimonial(idx)}
                className={`transition-all rounded-full ${
                  activeIndex === idx 
                    ? 'w-8 h-3 bg-primary-600' 
                    : 'w-3 h-3 bg-gray-300 hover:bg-gray-400'
                }`}
                aria-label={`Go to testimonial ${idx + 1}`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default TestimonialSlider;